/**
 * Quarantine review queue — list page.
 *
 * Routed at /quarantine. Lists uploads that failed malware scanning across
 * every agent's upload path, at either detection stage: `primary` (ClamAV,
 * synchronous, before the file ever reached storage) or `secondary` (the
 * cloud-native scanner on AWS/Azure flagging a file after ClamAV already
 * cleared it — see docs/malware-scanning-design.md). Mirrors
 * frontend/src/features/reviews/ (HITL gate queue) in shape, but is its own
 * feature — a quarantined upload has no confidence_score/reasoning_chain,
 * just a filename, a detected signature, which agent's upload path it came
 * from, and which gate caught it.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  quarantine,
  type QuarantineItemResponse,
  type QuarantineStatus,
} from "../../../apiv1";

interface Props {
  onBack?: () => void;
}

export function QuarantineList({ onBack }: Props) {
  const navigate = useNavigate();
  const [items, setItems] = useState<QuarantineItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<QuarantineStatus | "all">("quarantined");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await quarantine.listItems(
        filter === "all"
          ? { pageSize: 100 }
          : { status: filter, pageSize: 100 },
      );
      setItems(resp.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  function formatRelative(iso: string): string {
    const dt = new Date(iso);
    const ms = Date.now() - dt.getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  const filters: Array<{ key: QuarantineStatus | "all"; label: string }> = [
    { key: "quarantined", label: "Quarantined" },
    { key: "released", label: "Released" },
    { key: "deleted", label: "Deleted" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Quarantine</h1>
          <p className="text-sm text-white/50 mt-1">
            Uploads that failed malware scanning, held for review before release
            or deletion
          </p>
        </div>
        <div className="flex gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs rounded ${filter === f.key ? "bg-brand-500 text-white" : "bg-white/10 hover:bg-white/15"}`}
            >
              {f.label}
            </button>
          ))}
          {onBack && (
            <button
              onClick={onBack}
              className="px-3 py-1.5 text-xs bg-white/10 hover:bg-white/15 rounded ml-2"
            >
              Back
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-white/40 text-sm">
          No {filter === "all" ? "" : `${filter} `}items.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 border-b border-white/10">
              <th className="py-2 pr-4">Filename</th>
              <th className="py-2 pr-4">Source</th>
              <th className="py-2 pr-4">Stage</th>
              <th className="py-2 pr-4">Signature</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Size</th>
              <th className="py-2 pr-4">Uploaded</th>
              <th className="py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                onClick={() => navigate(`/quarantine/${item.id}`)}
                data-testid={`quarantine-row-${item.id}`}
              >
                <td className="py-2 pr-4 font-mono text-xs break-all">
                  {item.original_filename}
                </td>
                <td className="py-2 pr-4 text-xs text-white/60">
                  {item.source_module}
                </td>
                <td className="py-2 pr-4">
                  {item.detection_stage === "secondary" ? (
                    <span
                      className="text-orange-300"
                      title="Flagged by the cloud-native secondary scanner after the primary (ClamAV) gate had already cleared this file"
                    >
                      secondary
                    </span>
                  ) : (
                    <span className="text-white/50">primary</span>
                  )}
                </td>
                <td className="py-2 pr-4 font-mono text-xs text-red-300">
                  {item.detected_signature}
                </td>
                <td className="py-2 pr-4">
                  {item.status === "quarantined" ? (
                    <span className="text-amber-300">quarantined</span>
                  ) : item.status === "released" ? (
                    <span className="text-emerald-400">released</span>
                  ) : (
                    <span className="text-white/40">deleted</span>
                  )}
                </td>
                <td className="py-2 pr-4 font-mono text-xs">
                  {(item.size_bytes / 1024).toFixed(1)} KB
                </td>
                <td className="py-2 pr-4 text-xs text-white/60">
                  {formatRelative(item.created_at)}
                </td>
                <td className="py-2 text-right text-xs text-brand-400">
                  Review →
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
